import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

/** Calendar glyph — matches MeetAI IC.cal stroke style + soft header shade. */
function CalGlyph({ color = "currentColor", size = 16, accent = false }) {
  const stroke = color;
  const header = accent ? "rgba(8,117,225,0.18)" : "rgba(95,118,144,0.14)";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      {/* Soft month-header fill — aligns with field / page muted shade */}
      <path
        d="M5 5.5h14A1.5 1.5 0 0120.5 7v2.25H3.5V7A1.5 1.5 0 015 5.5z"
        fill={header}
      />
      <path
        d="M8 3.25v3.5M16 3.25v3.5M4.75 9.75h14.5M5.5 5.25h13A1.75 1.75 0 0120.25 7v12.25A1.75 1.75 0 0118.5 21H5.5A1.75 1.75 0 013.75 19.25V7A1.75 1.75 0 015.5 5.25z"
        stroke={stroke}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Day dots — clearer “calendar” read at small sizes */}
      <circle cx="8.25" cy="13.25" r="1" fill={stroke} />
      <circle cx="12" cy="13.25" r="1" fill={stroke} />
      <circle cx="15.75" cy="13.25" r="1" fill={stroke} />
      <circle cx="8.25" cy="16.75" r="1" fill={stroke} />
      <circle cx="12" cy="16.75" r="1" fill={stroke} />
    </svg>
  );
}

/**
 * Left calendar icon that opens the responsive MUI DatePicker
 * (desktop popover / mobile modal). No visible "Pick a date" field —
 * sits inside date-filter-shell next to the All dropdown.
 */
export default function MeetAiResponsiveDatePicker({
  valueYmd = null,
  onChangeYmd,
  disabled = false,
  className = "",
  picked = false,
  iconSize = 16,
  iconColor,
  title = "Pick a date",
}) {
  const [open, setOpen] = useState(false);

  const value = useMemo(() => {
    const raw = String(valueYmd || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const d = dayjs(raw);
    return d.isValid() ? d : null;
  }, [valueYmd]);

  const active = picked || !!value;
  const color = iconColor || (active ? "#0875E1" : "#5F7690");

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <span className={`meetai-responsive-datepicker meetai-responsive-datepicker--icon ${className}`.trim()}>
        <button
          type="button"
          className={`strip-cal-btn${active ? " picked" : ""}${disabled ? " is-disabled" : ""}`}
          title={title}
          aria-label={title}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) setOpen(true);
          }}
        >
          <CalGlyph color={color} size={iconSize} accent={active || open} />
        </button>

        {/* Hidden field — only the responsive picker UI is shown when open */}
        <DatePicker
          open={open}
          onOpen={() => !disabled && setOpen(true)}
          onClose={() => setOpen(false)}
          value={value}
          disabled={disabled}
          onChange={(next) => {
            if (!next || typeof next.isValid !== "function" || !next.isValid()) return;
            onChangeYmd?.(next.format("YYYY-MM-DD"));
            setOpen(false);
          }}
          desktopModeMediaQuery="@media (pointer: fine)"
          slotProps={{
            textField: {
              sx: {
                position: "absolute",
                width: 0,
                height: 0,
                opacity: 0,
                overflow: "hidden",
                pointerEvents: "none",
              },
              inputProps: { tabIndex: -1, "aria-hidden": true },
            },
            openPickerButton: { sx: { display: "none" } },
            inputAdornment: { sx: { display: "none" } },
          }}
        />
      </span>
    </LocalizationProvider>
  );
}
